import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("aero:p-3 aero:w-60", className)}
      classNames={{
        months: "aero:flex aero:flex-col aero:sm:flex-row aero:gap-2",
        month: "aero:flex aero:flex-col aero:gap-4",
        caption:
          "aero:flex aero:justify-center aero:pt-1 aero:relative aero:items-center aero:w-full",
        caption_label: "aero:text-sm aero:font-medium",
        nav: "aero:flex aero:items-center aero:gap-1",
        nav_button: cn(
          buttonVariants({
            intent: "primary",
            design: "outline",
            shape: "rounded",
            size: "sm",
          }),
          "aero:size-7 aero:bg-transparent aero:p-0 aero:opacity-50 aero:hover:opacity-100"
        ),
        nav_button_previous: "aero:absolute aero:left-1",
        nav_button_next: "aero:absolute aero:right-1",
        table: "aero:w-full aero:border-collapse aero:space-x-1",
        head_row: "aero:flex",
        head_cell:
          "aero:text-muted-foreground aero:!rounded-md aero:w-8 aero:font-normal aero:text-[0.8rem]",
        row: "aero:flex aero:w-full aero:mt-2",
        cell: cn(
          "aero:relative aero:p-0 aero:text-center aero:text-sm aero:focus-within:relative aero:focus-within:z-20",
          props.mode === "range"
            ? "aero:[&:has(>.day-range-end)]:rounded-r-md aero:[&:has(>.day-range-start)]:rounded-l-md aero:first:[&:has([aria-selected])]:rounded-l-md aero:last:[&:has([aria-selected])]:rounded-r-md"
            : "aero:[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({
            intent: "primary",
            design: "text",
            shape: "rounded",
            size: "sm",
          }),
          "aero:size-8 aero:p-0 aero:font-normal aero:aria-selected:opacity-100"
        ),
        day_range_start:
          "aero:day-range-start aero:aria-selected:bg-brand-primary-600 aero:aria-selected:text-white aero:rounded-md",
        day_range_end:
          "aero:day-range-end aero:aria-selected:bg-brand-primary-600 aero:aria-selected:text-white aero:rounded-md",
        day_selected:
          "aero:bg-brand-primary-600 aero:text-white aero:hover:bg-brand-primary-700 aero:hover:text-white aero:focus:bg-brand-primary-600 aero:focus:text-white",
        day_today: "aero:bg-accent aero:text-accent-foreground",
        day_outside:
          "aero:day-outside aero:text-muted-foreground aero:aria-selected:text-muted-foreground",
        day_disabled: "aero:text-muted-foreground aero:opacity-50",
        day_range_middle:
          "aero:aria-selected:bg-neutral-100 aero:aria-selected:text-foreground aero:rounded-none",
        day_hidden: "aero:invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("aero:size-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("aero:size-4", className)} {...props} />
        ),
      }}
      {...props}
    />
  );
}

export { Calendar };
